import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { Search, CornerDownLeft } from 'lucide-react';
import type { EntitySummary } from '@hc/shared';
import { listEntities } from '../api/client.js';
import { clusterByGroup, GroupHeader, GROUP_MEMBER_INDENT } from './entity-search-grouping.js';
import { useActiveTabId, useAppStore } from './store.js';
import { useDockviewApi } from './dockview-context.js';
import { useOpenCompanyWindow } from './use-open-company-overview.js';
import { PANEL_REGISTRY, type PanelDef, type PanelCategory } from './panel-registry.js';
import type { PanelComponentKey, PanelParams } from './components.js';

// Cmd+K (Ctrl+K on Windows) command palette — the platform's
// discoverability surface.
//
// Two display modes:
//   - Catalogue (empty query): every panel the user can open, grouped
//     by category. This is how new users learn what's available without
//     having to read docs.
//   - Mixed search (1+ chars): filter panel titles client-side; at 2+
//     chars, also fetch entities from the API. Both result kinds share
//     a single flat keyboard cursor.
//
// Row activation routing:
//   - panel row     → store.addPanel + dockview.addPanel into the
//                     active tab
//   - entity row    → push entity onto the active tab's default link
//                     group (every linked panel refocuses)

const DEBOUNCE_MS = 150;
const ENTITY_SEARCH_MIN_CHARS = 2;
const RESULT_LIMIT = 25;

type Item = { kind: 'panel'; panel: PanelDef } | { kind: 'entity'; entity: EntitySummary };

const CATEGORY_LABEL: Record<PanelCategory, string> = {
  workflow: 'Workflow',
  discovery: 'Discovery',
  map: 'Map',
  'morning-brief': 'Morning brief',
  help: 'Reference',
};

export interface CommandPaletteProps {
  /** Optional controlled-open prop. When supplied, the palette no longer
   *  installs its own Cmd+K listener — the parent (HotkeyProvider) does. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CommandPalette(props: CommandPaletteProps = {}) {
  const controlled = props.open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlled ? props.open! : internalOpen;
  const setOpen = (next: boolean | ((o: boolean) => boolean)) => {
    if (controlled) {
      const resolved = typeof next === 'function' ? next(open) : next;
      props.onOpenChange?.(resolved);
    } else {
      setInternalOpen(next);
    }
  };
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const activeTabId = useActiveTabId();
  const addPanelToStore = useAppStore((s) => s.actions.addPanel);
  const apiRef = useDockviewApi();
  const openCompanyWindow = useOpenCompanyWindow();

  // Self-installed Cmd/Ctrl+K listener — only when uncontrolled. When the
  // parent controls open state it also owns the keyboard shortcut to
  // avoid two listeners fighting each other.
  useEffect(() => {
    if (controlled) return undefined;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setInternalOpen((o) => !o);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [controlled]);

  // Debounce the query.
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query]);

  // Reset state every time the palette opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
      setActiveIndex(0);
      window.requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Catalogue base: every panel that can stand on its own (excludes
  // panels which need a pre-bound entity to render anything useful).
  const catalogue = useMemo(() => PANEL_REGISTRY.filter((p) => !p.needsEntity), []);

  // Client-side panel filtering. Substring match on title; case-
  // insensitive. Triggers as soon as the user types one char so the
  // user gets instant feedback even before the entity API is hit.
  const filteredPanels = useMemo(() => {
    const q = debounced.toLowerCase();
    if (!q) return catalogue;
    return catalogue.filter((p) => p.title.toLowerCase().includes(q));
  }, [catalogue, debounced]);

  // Entity search — only fires for >= 2 chars, to avoid hammering the
  // API on every keystroke.
  const entities = useQuery({
    queryKey: ['command-palette', 'search', debounced],
    queryFn: () => listEntities({ q: debounced, limit: RESULT_LIMIT }),
    enabled: debounced.length >= ENTITY_SEARCH_MIN_CHARS,
    staleTime: 60_000,
  });
  const entityRawRows =
    debounced.length >= ENTITY_SEARCH_MIN_CHARS ? (entities.data?.data ?? []) : [];

  // Group-aware clustering: hits that belong to a resolved roll-up group are
  // clustered under a group header (rendered below); `flat` is the same rows
  // in cluster-contiguous order so keyboard indices stay a simple flat list.
  const { clusters: entityClusters, flat: entityRows } = useMemo(
    () => clusterByGroup(entityRawRows, (e) => e.economicGroup),
    [entityRawRows],
  );

  // Flat item list — drives both rendering (with section headers
  // interpolated by index) and keyboard navigation.
  const items = useMemo<Item[]>(() => {
    const out: Item[] = filteredPanels.map((p) => ({ kind: 'panel' as const, panel: p }));
    for (const e of entityRows) out.push({ kind: 'entity' as const, entity: e });
    return out;
  }, [filteredPanels, entityRows]);

  // Clamp activeIndex whenever items shrink (e.g., typing narrows the list).
  useEffect(() => {
    setActiveIndex((i) => {
      if (items.length === 0) return 0;
      return Math.min(i, items.length - 1);
    });
  }, [items.length]);

  function openPanel(def: PanelDef) {
    if (!activeTabId) return;
    const api = apiRef.current;
    if (!api) return;
    const panelId = addPanelToStore(activeTabId, {
      component: def.id,
      title: def.title,
    });
    // Spawn as a floating overlay so the new panel is unmistakably
    // "new" — it lands above the existing layout, ready for the
    // user to position. Drag the header onto a group / group edge
    // / canvas to dock it. Cascade by 24px per existing floating
    // panel so successive palette opens don't sit on top of each
    // other.
    const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
    const offset = 24 * cascade;
    api.addPanel<PanelParams>({
      id: panelId,
      component: def.id as PanelComponentKey,
      title: def.title,
      params: { tabId: activeTabId, panelId },
      floating: {
        position: { left: 120 + offset, top: 80 + offset },
        width: 520,
        height: 400,
      },
    });
    setOpen(false);
  }

  function pickEntity(entityId: string) {
    if (!activeTabId) return;
    // Picking an entity from the palette always pops a fresh, independent
    // Record panel — it must never silently rewrite an existing Record
    // that the user has open on a different company (that felt like the
    // panel "took over" without warning). Use the always-new/pinned path,
    // same as the map's "Company information" popup.
    openCompanyWindow(entityId);
    setOpen(false);
  }

  function activate(item: Item) {
    if (item.kind === 'panel') openPanel(item.panel);
    else pickEntity(item.entity.id);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(items.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[activeIndex];
      if (item) activate(item);
    }
  }

  const panelCount = filteredPanels.length;
  const entityCount = entityRows.length;
  const isSearching = debounced.length >= ENTITY_SEARCH_MIN_CHARS && entities.isLoading;
  const noResults = items.length === 0 && !isSearching;

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.32)',
            zIndex: 9499,
          }}
        />
        <Dialog.Content
          aria-describedby={undefined}
          style={{
            position: 'fixed',
            top: '15%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '620px',
            maxWidth: '92vw',
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md, 6px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            zIndex: 9500,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '64vh',
          }}
        >
          <Dialog.Title style={{ position: 'absolute', clipPath: 'inset(100%)' }}>
            Open panel or jump to entity
          </Dialog.Title>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)',
              borderBottom: '1px solid var(--border-default)',
              // White search-bar fill — matches the screener search and
              // top-bar cmd-K trigger.
              background: 'var(--menu-surface)',
            }}
          >
            <Search size={14} style={{ opacity: 0.6 }} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Open panel, or search entities by name / registry id…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--fg-default)',
              }}
            />
            <span
              style={{
                fontFamily: 'var(--font-mono, monospace)',
                fontSize: '11px',
                color: 'var(--fg-muted)',
                opacity: 0.7,
              }}
            >
              ⌘K
            </span>
          </div>
          <div
            ref={listRef}
            role="listbox"
            style={{ overflowY: 'auto', flex: 1, padding: 'var(--space-1) 0' }}
          >
            {panelCount > 0 && <SectionLabel>Open panel</SectionLabel>}
            {filteredPanels.map((panel, i) => {
              const idx = i; // panel rows occupy [0, panelCount)
              return (
                <Row
                  key={panel.id}
                  active={idx === activeIndex}
                  primary={panel.title}
                  secondary={CATEGORY_LABEL[panel.category]}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => activate({ kind: 'panel', panel })}
                />
              );
            })}

            {entityCount > 0 && <SectionLabel>Entity</SectionLabel>}
            {(() => {
              // Render clusters: group header (non-interactive) + members,
              // keeping the flat keyboard index in step with entityRows.
              let idx = panelCount - 1;
              return entityClusters.map((cluster) => (
                <div key={cluster.group ?? `solo-${cluster.rows[0]!.id}`}>
                  {cluster.group && (
                    <GroupHeader group={cluster.group} count={cluster.rows.length} />
                  )}
                  {cluster.rows.map((entity) => {
                    idx += 1;
                    const rowIdx = idx;
                    const meta = [
                      entity.countryCode,
                      entity.registryId,
                      entity.subSegment ?? '—',
                    ].join(' · ');
                    return (
                      <Row
                        key={entity.id}
                        active={rowIdx === activeIndex}
                        primary={entity.legalName ?? '(no name)'}
                        secondary={meta}
                        monoSecondary
                        indent={cluster.group ? GROUP_MEMBER_INDENT : 0}
                        onMouseEnter={() => setActiveIndex(rowIdx)}
                        onClick={() => activate({ kind: 'entity', entity })}
                      />
                    );
                  })}
                </div>
              ));
            })()}

            {isSearching && <Hint>Searching entities…</Hint>}
            {noResults && (
              <Hint>
                {debounced.length >= ENTITY_SEARCH_MIN_CHARS
                  ? 'No panels or entities match.'
                  : 'No panels match. Type 2+ chars to search entities.'}
              </Hint>
            )}
          </div>
          <Footer />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Row({
  active,
  primary,
  secondary,
  monoSecondary,
  indent,
  onMouseEnter,
  onClick,
}: {
  active: boolean;
  primary: string;
  secondary: string;
  monoSecondary?: boolean;
  /** Left inset for group-member rows (nested under a GroupHeader). */
  indent?: number;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  return (
    <button
      role="option"
      aria-selected={active}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 18px',
        alignItems: 'baseline',
        width: '100%',
        gap: 'var(--space-3)',
        padding: 'var(--space-1) var(--space-3)',
        paddingLeft: indent ? `calc(var(--space-3) + ${indent}px)` : undefined,
        border: 'none',
        background: active ? 'var(--bg-hover, rgba(0,0,0,0.04))' : 'transparent',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--fg-default)',
        }}
      >
        {primary}
      </span>
      <span
        style={{
          fontFamily: monoSecondary ? 'var(--font-mono, monospace)' : undefined,
          fontSize: '11px',
          color: 'var(--fg-muted)',
          whiteSpace: 'nowrap',
        }}
      >
        {secondary}
      </span>
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          opacity: active ? 0.9 : 0,
          color: 'var(--fg-muted)',
        }}
      >
        <CornerDownLeft size={11} />
      </span>
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: '9px',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--fg-muted)',
        padding: 'var(--space-2) var(--space-3) var(--space-1)',
      }}
    >
      {children}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 'var(--space-3) var(--space-4)',
        color: 'var(--fg-muted)',
        fontSize: 'var(--font-size-sm)',
      }}
    >
      {children}
    </div>
  );
}

function Footer() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-1) var(--space-3)',
        borderTop: '1px solid var(--border-default)',
        fontSize: '10px',
        color: 'var(--fg-muted)',
        letterSpacing: '0.02em',
      }}
    >
      <FooterKey hint="↑↓" label="navigate" />
      <FooterKey hint="↵" label="select" />
      <FooterKey hint="esc" label="dismiss" />
    </div>
  );
}

function FooterKey({ hint, label }: { hint: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
      <kbd
        style={{
          fontFamily: 'var(--font-mono, monospace)',
          fontSize: '10px',
          padding: '0 4px',
          border: '1px solid var(--border-default)',
          borderRadius: 2,
          color: 'var(--fg-default)',
          background: 'var(--bg-canvas, transparent)',
        }}
      >
        {hint}
      </kbd>
      <span>{label}</span>
    </span>
  );
}
